import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Card,
  Table,
  Stack,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  Box,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Grid,
  FormHelperText,
  LinearProgress,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
// components
import Iconify from '../../../../components/iconify';
import Scrollbar from '../../../../components/scrollbar';
// sections
import ProjectListHead from '../../list/components/ProjectListHead';
import { deleteProjectMember, editProjectMember, getMemberRole } from '../../../../services/projectService';
import {
  GUEST,
  PROJECT_EDITOR_ROLE,
  ROLE_CAN_REMOVE_PROJECT,
  ROLE_LEVEL,
} from '../../../../Constants/ProjectConstants';
import OpenDialogBox from './DeleteModal';
import style from './ProjectSetting.module.css';
import NoMembersFoundMessage from './NoMemberFoundMessage';
import ModalComponent from '../../../../components/modal-layout/ModalComponent';
import ProjectMemberSchema from '../../schema/projectMemberSchema';
import { convertHM } from '../../../../utils/timeCalculation';
import { isAdmin } from '../../../../utils/share';
// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'hours', label: 'Allotted Hours', alignRight: false },
  { id: 'hours', label: 'Consumed Hours', alignRight: false },
  { id: 'hours', label: 'Status', alignRight: false },
  { id: 'action', label: '' },
];

// ----------------------------------------------------------------------
function ProjectMemberList({
  isEditable,
  projectDetails,
  employeeList,
  fetchProjectDetails,
  setUserRole,
  userRole,
  projectRoles,
}) {
  const { id } = useParams();
  const [open, setOpen] = useState(null);
  const [openModal, setModalOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [action, setAction] = useState('');
  const [page] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState();
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage] = useState(25);
  const [totalCount] = useState([]);
  const defaultFormData = useRef({
    userId: undefined,
    userHours: undefined,
    projectRole: undefined,
  });
  const handleOpen = () => setModalOpen(true);
  const handleClose = () => {
    setValues({ userId: '', userHours: '', projectRole: '' });
    setTouched({});
    setModalOpen(false);
  };

  const handleDialogOpen = () => {
    setOpen(null);
    setOpenDialog(true);
  };
  const handleDialogClose = () => setOpenDialog(false);

  const handleUpdate = () => {
    setOpen(null);
    handleOpen();
    setAction('update');
    defaultFormData.current = {
      userId: selected?.userId,
      userHours: selected?.userHours,
      id: selected?.id,
      projectId: selected?.projectId,
      projectRoleId: selected?.projectRoleId,
    };
    setValues({
      userId: selected?.userId,
      userHours: selected?.userHours,
      id: selected?.id,
      projectId: selected?.projectId,
      projectRoleId: selected?.projectRoleId,
    });
  };
  const handleDelete = async () => {
    try {
      const Project = await deleteProjectMember(id, selected?.id);
      toast.success(Project?.data?.message);
      handleDialogClose();
      await fetchProjectDetails(id);
    } catch (error) {
      toast.error('Error while deleting');
      console.error(error);
    }
  };
  const handleOpenMenu = (event, row) => {
    setOpen(event.currentTarget);
    setSelected(row);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalCount) : 0;

  let totalAllotedHours = 0;

  if (projectDetails?.projectmember?.length > 0) {
    totalAllotedHours = projectDetails?.projectmember?.reduce((sum, member) => sum + member?.totalTime / 3600, 0);
  }

  const formik = useFormik({
    initialValues: defaultFormData.current,
    validationSchema: ProjectMemberSchema(projectDetails, totalAllotedHours, selected),
    onSubmit: (values) => {
      handleAPICall(values);
    },
  });
  const handleAPICall = async (values) => {
    try {
      let Project;
      if (action === 'update') {
        Project = await editProjectMember(values, id);
        const roles = await getMemberRole(id);
        setUserRole(roles.data.data);
      }
      await fetchProjectDetails(id);
      toast.success(Project?.data?.message);
      handleClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };
  const {
    touched,
    handleSubmit,
    setValues,
    errors,
    isSubmitting,
    setSubmitting,
    getFieldProps,
    setTouched,
    setFieldValue,
  } = formik;

  const projectRoleList =
    selected?.projectRoles?.roleName === GUEST
      ? projectRoles?.filter((role) => role.roleName === GUEST)
      : projectRoles?.filter(
          (role) =>
            role.roleName !== GUEST && (!(ROLE_LEVEL[userRole?.roleName] > ROLE_LEVEL[role?.roleName]) || isAdmin())
        );

  let employeeOptions;
  if (action === 'update') {
    employeeOptions = employeeList;
  } else {
    const membersList = projectDetails?.projectmember?.map((member) => member?.userId);

    const filteredEmployees = employeeList
      .filter((emp) => !membersList.includes(emp.id))
      .sort((a, b) => a.userName.localeCompare(b.userName));

    employeeOptions = filteredEmployees;
  }

  return (
    <>
      <Container className={style.memberTable}>
        <Typography variant="h4" gutterBottom>
          Project Members
        </Typography>
        <Scrollbar>
          <Card>
            <TableContainer>
              <Table>
                <ProjectListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={totalCount}
                  onRequestSort={handleRequestSort}
                  sortable={false}
                />
                {projectDetails?.projectmember?.length ? (
                  <TableBody>
                    {projectDetails?.projectmember?.map((row, index) => {
                      const projectPercentage =
                        row?.userHours === 0 ? 0 : ((parseFloat(row?.totalTime, 10) / 3600) * 100) / row?.userHours;
                      return (
                        <TableRow hover tabIndex={-1} role="checkbox" key={index}>
                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {row?.user?.userName}
                            </Typography>
                          </TableCell>
                          <TableCell align="left"> {row?.projectRoles?.roleName}</TableCell>
                          <TableCell align="left"> {row?.userHours}</TableCell>
                          <TableCell align="left"> {convertHM(Math.round(parseInt(row?.totalTime, 10)))}</TableCell>
                          <TableCell align="left">
                            <LinearProgress
                              variant="determinate"
                              value={projectPercentage > 100 ? 100 : projectPercentage}
                              color={projectPercentage < 71 ? 'success' : projectPercentage < 91 ? 'warning' : 'error'}
                              sx={{
                                height: 20,
                                border: projectPercentage < 91 ? undefined : '1px solid #e43b3b',
                                top: 13,
                              }}
                            />
                            <Typography
                              variant="body2"
                              style={{ marginLeft: '103%', color: 'black', marginTop: '-7px' }}
                              color="text.secondary"
                            >{`${Math.round(projectPercentage)}%`}</Typography>
                          </TableCell>
                          {isEditable ? (
                            <TableCell align="right">
                              {isAdmin() || PROJECT_EDITOR_ROLE.includes(userRole?.roleName) ? (
                                <IconButton
                                  size="large"
                                  color="inherit"
                                  onClick={(e) => handleOpenMenu(e, row)}
                                  disabled={
                                    !isAdmin() &&
                                    ROLE_LEVEL[userRole?.roleName] > ROLE_LEVEL[row?.projectRoles?.roleName]
                                  }
                                >
                                  <Iconify icon={'eva:more-vertical-fill'} />
                                </IconButton>
                              ) : null}
                            </TableCell>
                          ) : (
                            <TableCell />
                          )}
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow className={style.tableRow * emptyRows}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                ) : (
                  <NoMembersFoundMessage />
                )}
              </Table>
            </TableContainer>
          </Card>
        </Scrollbar>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          className: style.menuPopover,
        }}
      >
        {isAdmin() || PROJECT_EDITOR_ROLE.includes(userRole?.roleName) ? (
          <MenuItem onClick={handleUpdate}>
            <Iconify icon={'eva:edit-fill'} mr={2} />
            Edit
          </MenuItem>
        ) : null}
        {isAdmin() || ROLE_CAN_REMOVE_PROJECT.includes(userRole?.roleName) ? (
          <MenuItem
            onClick={handleDialogOpen}
            sx={{
              color: selected?.totalTime > 0 ? undefined : 'error.main',
            }}
            disabled={selected?.totalTime > 0}
          >
            <Iconify icon={'eva:trash-2-outline'} mr={2} />
            Delete
          </MenuItem>
        ) : null}
      </Popover>
      <ModalComponent openModal={openModal} handleClose={handleClose}>
        <Box className={`${style.container} ${style.inputBoxStyle}`}>
          <Typography variant="h5" align="center" className={style.memberUpdateModelTitle}>
            Edit Assigned project hours
          </Typography>
          <Box className={`${style.Container} ${style.inputDiv}`}>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
        </Box>
        <Divider mb={2} />
        <Container maxWidth="sm" sx={{ mt: 2 }}>
          <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>User</InputLabel>
                  <Select
                    {...getFieldProps('userId')}
                    error={touched.userId && Boolean(errors.userId)}
                    helperText={touched.userId && errors.userId}
                    disabled={action === 'update'}
                    label="User"
                    onChange={(event, value) => {
                      setFieldValue('userId', value?.props?.value);
                    }}
                  >
                    {employeeOptions.map((employee, i) => (
                      <MenuItem value={employee.id} key={i}>
                        {employee.userName}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.userId && errors.userId && (
                    <FormHelperText className={style.helperText}>{errors.userId}</FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...getFieldProps('projectRoleId')}
                    error={touched.projectId && Boolean(errors.projectId)}
                    helperText={touched.projectId && errors.projectId}
                    label="Role"
                    onChange={(event, value) => {
                      setFieldValue('projectRoleId', value?.props?.value);
                    }}
                    disabled={selected?.projectRoles?.roleName === GUEST}
                  >
                    {projectRoleList.map((role, i) => (
                      <MenuItem value={role.id} key={i}>
                        {role.roleName}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.projectId && errors.projectId && (
                    <FormHelperText className={style.helperText}>{errors.projectId}</FormHelperText>
                  )}
                </FormControl>
                <Grid container justifyContent={'space-between'}>
                  <Grid item>
                    <TextField
                      label="Project Available Hours"
                      value={projectDetails?.totalHrs - totalAllotedHours}
                      disabled
                    />
                  </Grid>
                  <Grid item>
                    <TextField label="Project Consumed Hours" value={totalAllotedHours ?? 0} disabled />
                  </Grid>
                </Grid>

                <TextField
                  label="Add Hours"
                  type="number"
                  {...getFieldProps('userHours')}
                  error={touched.userHours && Boolean(errors.userHours)}
                  helperText={touched.userHours && errors.userHours}
                  fullWidth
                />
                <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                  Save
                </LoadingButton>
              </Stack>
            </Form>
          </FormikProvider>
        </Container>
      </ModalComponent>
      <OpenDialogBox openDialog={openDialog} handleDialogClose={handleDialogClose} handleDelete={handleDelete} />
    </>
  );
}

ProjectMemberList.propTypes = {
  isEditable: PropTypes.bool,
  projectDetails: PropTypes.object,
  employeeList: PropTypes.array,
  filteredEmployeeList: PropTypes.array,
  fetchProjectDetails: PropTypes.func,
  setUserRole: PropTypes.func,
  userRole: PropTypes.object,
  projectRoles: PropTypes.array,
};

export default ProjectMemberList;
